"use client"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { usePathname } from "next/navigation"

interface ILoadingProps {
	message?: string
	fullScreen?: boolean
}

export default function Loading({
	message = "Loading...",
	fullScreen = true,
}: ILoadingProps = {}) {
	const [mounted, setMounted] = useState(false)
	const [showLoading, setShowLoading] = useState(false)
	const pathname = usePathname()

	useEffect(() => {
		setMounted(true)
		
		// Don't show loading for initial home page load
		const isHomePage = pathname === "/" || pathname === ""
		const isFirstLoad = !window.performance.getEntriesByType("navigation")[0] || 
			(window.performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming).type === "navigate"
		
		if (isHomePage && isFirstLoad) {
			setShowLoading(false)
		} else {
			// Add a small delay to prevent flash for fast page loads
			const timer = setTimeout(() => {
				setShowLoading(true)
			}, 100)
			
			return () => clearTimeout(timer)
		}
		
		return () => setMounted(false)
	}, [pathname])

	const LoadingContent = () => (
		<div className="relative">
			{/* Spinner */}
			<div
				className="h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent"
				aria-hidden="true"
			/>

			{/* Inner spinning circle */}
			<div
				className="absolute inset-0 flex items-center justify-center"
				aria-hidden="true"
			>
				<div className="h-8 w-8 animate-[spin_1.5s_linear_infinite] rounded-full border-4 border-primary/40 border-t-transparent" />
			</div>

			{/* Progress bar */}
			<div
				className="-bottom-8 -translate-x-1/2 absolute left-1/2 h-1 w-24"
				aria-hidden="true"
			>
				<div className="h-full w-full animate-loading-bar bg-primary" />
			</div>

			{/* Screen reader text */}
			<span className="sr-only">{message}</span>
		</div>
	)

	if (!mounted || !showLoading) {
		return null
	}

	if (fullScreen) {
		return createPortal(
			<div
				className="fixed inset-0 z-50 flex items-center justify-center bg-background/50 backdrop-blur-sm"
				role="alert"
				aria-busy="true"
				aria-label={message}
			>
				<LoadingContent />
			</div>,
			document.body,
		)
	}

	return (
		<div
			className="relative flex items-center justify-center p-4"
			role="alert"
			aria-busy="true"
			aria-label={message}
		>
			<LoadingContent />
		</div>
	)
}
